import * as Dialog from '@radix-ui/react-dialog';
import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { Button } from '~/chords/Button/Button';
import { IconButton } from '~/chords/IconButton';
import { cn } from '~/lib/cn';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { IntroIllustration } from '~/modules/navigation/components/MusicIQIntroModal/Illustrations/IntroIllustration';
import { LocationIllustration } from '~/modules/navigation/components/MusicIQIntroModal/Illustrations/LocationIllustration';

// Slide data

interface Slide {
  titleKey: string;
  descriptionKey: string;
  illustration: JSX.Element;
  showTitleIcon?: boolean;
}

const SLIDES: Slide[] = [
  {
    titleKey: 'slide1.title',
    descriptionKey: 'slide1.description',
    illustration: <IntroIllustration />,
    showTitleIcon: true,
  },
  {
    titleKey: 'slide2.title',
    descriptionKey: 'slide2.description',
    illustration: <LocationIllustration />,
  },
];

// Modal

const MUSIC_IQ_MODAL_STORAGE_KEY = 'hasViewedMusicIqIntroModal';

export function MusicIQIntroModal() {
  const { pathname } = useLocation();
  const { t } = useTranslation('wmgone', {
    keyPrefix: 'musicIqIntroModal',
  });
  const [isMusicIqChatbotEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgDataMusicIqChatbotEnabled,
  ]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;

  useEffect(() => {
    const inHome = pathname === '/home' || pathname === '/';
    const hasViewedMusicIqIntroModal = localStorage.getItem(
      MUSIC_IQ_MODAL_STORAGE_KEY,
    );

    setOpen(hasViewedMusicIqIntroModal !== 'true' && inHome);
  }, [pathname]);

  const dismiss = () => {
    setStep(0);
    setOpen(false);
  };

  const markAsViewed = () => {
    localStorage.setItem(MUSIC_IQ_MODAL_STORAGE_KEY, 'true');
  };

  const handleBack = () => {
    setStep((s) => Math.max(0, s - 1));
  };

  const handleNext = () => {
    setStep((s) => Math.min(SLIDES.length - 1, s + 1));
  };

  const handleTryMusicIQ = () => {
    markAsViewed();
    dismiss();
  };

  if (!isMusicIqChatbotEnabled) {
    return null;
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          dismiss();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[100] backdrop-blur transition-opacity duration-200 bg-transparent"
          style={{ opacity: open ? 1 : 0 }}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[101] w-[648px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2',
            'transition-opacity duration-200',
            'focus:outline-none',
          )}
          style={{ opacity: open ? 1 : 0 }}
        >
          {/* Modal card */}
          <div className="relative overflow-hidden rounded-md shadow-2xl">
            {/* Close button — top-right corner of the whole modal */}
            <Dialog.Close asChild onClick={markAsViewed}>
              <IconButton
                iconType="xmark"
                iconVariant="regular"
                size="large"
                aria-label="Close"
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  zIndex: 10,
                  color: 'white',
                }}
              />
            </Dialog.Close>

            {/* Illustration area — ~390px tall to match reference */}
            <div
              className="relative flex h-[420px] items-center justify-center px-3xl py-lg"
              style={{
                background:
                  'linear-gradient(145deg, #061628 0%, #0a1f42 50%, #0d2450 100%)',
              }}
            >
              <div key={step} className="w-full">
                {slide.illustration}
              </div>
            </div>

            {/* Content area */}
            <div
              className="px-2xl pb-[20px] pt-xl"
              style={{ backgroundColor: '#1c1c1c' }}
            >
              <Dialog.Title className="mb-sm flex items-center gap-sm text-headline-sm font-semibold text-white">
                {t(slide.titleKey)}
                {slide.showTitleIcon ? (
                  <img
                    src="/images/music-iq/music-iq.svg"
                    alt="Music IQ Logo"
                    className="block size-[20px]"
                  />
                ) : null}
              </Dialog.Title>
              <Dialog.Description
                className="text-body-sm"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                {t(slide.descriptionKey)}
              </Dialog.Description>
            </div>

            {/* Footer row — step counter + Back/Next (from the 4-slide tour) */}
            <div
              className="flex items-center justify-between px-2xl pb-[28px] pt-[20px]"
              style={{
                backgroundColor: '#1c1c1c',
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <span className="text-body-sm text-subtle">
                {t('totalSlides', { count: step + 1, total: SLIDES.length })}
              </span>
              <div
                className="flex items-center gap-[20px]"
                style={{ color: 'rgba(255,255,255,0.8)' }}
              >
                {step > 0 && (
                  <Button variant="text" size="medium" onClick={handleBack}>
                    {t('back')}
                  </Button>
                )}
                {isLast ? (
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleTryMusicIQ}
                    asChild
                  >
                    <Link to="/data/music-iq">{t('tryMusicIQ')}</Link>
                  </Button>
                ) : (
                  <Button variant="primary" size="medium" onClick={handleNext}>
                    {t('next')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

MusicIQIntroModal.displayName = 'MusicIQIntroModal';
